import { useCallback, useEffect, useRef } from 'react';
import { FunctionWithNoArgs, VoidFunctionWithNoArgs } from './hookUtilityTypes';

interface UseTimeoutReturnType {
  /**
   * a function to clear a useTimeout callback so that it will never be called
   */
  clear: VoidFunctionWithNoArgs;

  /**
   * a function to restart the useTimeout timeout from the beginning
   */
  reset: VoidFunctionWithNoArgs;
}

/**
 * Invoke a callback after a delay, with returned functions to clear the callback and reset the delay.
 *
 * Note: changes to the callback will *not* reset the timeout. If the callback changes, the
 * *latest* supplied callback is the one that will be executed when the timeout expires. This
 * is a convenience and means that the callback function does not need to be wrapped in a
 * useCallback().
 *
 * @param callback called on timer expiry
 * @param delay the delay until the timer expires
 */
export function useTimeout(callback: FunctionWithNoArgs, delay: number): UseTimeoutReturnType {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const set = useCallback(() => {
    timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
  }, [delay]);

  const clear = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    set();
    return clear;
  }, [delay, set, clear]);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  return { reset, clear };
}

export default useTimeout;
